import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Icon from '@/components/AppIcon';
import LoadingState from '@/components/ui/LoadingState';
import { fetchInvitationDetails, completeEmployeeOnboarding } from '@/lib/api/employeeInvitations';
import { useAuth } from '@/context/AuthContext';
import Select from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import AuthBrandingPanel from '@/components/ui/AuthBrandingPanel';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => {
      const reason = reader.error ?? new Error('Failed to read file.');
      reject(reason);
    };
    reader.readAsDataURL(file);
  });

const normalizeInvitationValue = (value) =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : value;

const OnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submissionInFlight, setSubmissionInFlight] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [formValues, setFormValues] = useState({
    fullName: '',
    birthday: '',
    gender: '',
    password: '',
    confirmPassword: '',
    phone: '',
    addressLine: '',
    city: '',
    zipCode: '',
    shiftPreference: [],
    identityDocument: null,
    avatarImage: null,
    dataPrivacyApproval: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setFetchError(new Error('Missing onboarding token.'));
        setLoading(false);
        return;
      }
      setLoading(true);
      setFetchError(null);
      try {
        const details = await fetchInvitationDetails(token);
        setInvitation(details);
        setFormValues((prev) => ({
          ...prev,
          fullName: details?.invitedName ?? '',
        }));
      } catch (error) {
        setFetchError(error);
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleInputChange = (field) => (event) => {
    const target = event?.target;
    if (!target) {
      return;
    }
    let value = target?.value ?? '';
    if (target?.type === 'checkbox') {
      value = target?.checked ?? false;
    }
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (field, { multiple = false } = {}) => (event) => {
    const files = Array.from(event?.target?.files ?? []);
    setFormValues((prev) => ({
      ...prev,
      [field]: multiple ? files : files?.[0] ?? null,
    }));
  };

  const handleShiftPreferenceChange = (values) => {
    setFormValues((prev) => ({
      ...prev,
      shiftPreference: Array.isArray(values) ? values : [],
    }));
  };

  const handleGenderChange = (value) => {
    setFormValues((prev) => ({
      ...prev,
      gender: typeof value === 'string' ? value : '',
    }));
  };

  useEffect(() => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      if (prev?.password && formValues.password?.length >= 8) {
        delete next.password;
        changed = true;
      }

      if (formValues.confirmPassword) {
        if (formValues.password !== formValues.confirmPassword) {
          if (next.confirmPassword !== 'Passwords do not match.') {
            next.confirmPassword = 'Passwords do not match.';
            changed = true;
          }
        } else if (next.confirmPassword) {
          delete next.confirmPassword;
          changed = true;
        }
      } else if (next.confirmPassword) {
        delete next.confirmPassword;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [formValues.confirmPassword, formValues.password]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formValues.fullName?.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!formValues.birthday) {
      errors.birthday = 'Birthday is required.';
    }
    if (!formValues.gender) {
      errors.gender = 'Gender selection is required.';
    }
    if (!formValues.password || formValues.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }
    if (formValues.password !== formValues.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
    if (!formValues.phone?.trim()) {
      errors.phone = 'Phone number is required.';
    }
    if (!formValues.addressLine?.trim()) {
      errors.addressLine = 'Home address is required.';
    }
    if (!formValues.city?.trim()) {
      errors.city = 'City is required.';
    }
    if (!formValues.zipCode?.trim()) {
      errors.zipCode = 'ZIP Code is required.';
    }
    if (!formValues.shiftPreference || formValues.shiftPreference?.length === 0) {
      errors.shiftPreference = 'Select at least one shift preference.';
    }
    if (!formValues.avatarImage) {
      errors.avatarImage = 'Avatar image is required.';
    }
    if (!formValues.dataPrivacyApproval) {
      errors.dataPrivacyApproval = 'You must agree to the data privacy policy to continue.';
    }
    return errors;
  }, [
    formValues.addressLine,
    formValues.avatarImage,
    formValues.birthday,
    formValues.city,
    formValues.confirmPassword,
    formValues.dataPrivacyApproval,
    formValues.fullName,
    formValues.gender,
    formValues.password,
    formValues.phone,
    formValues.shiftPreference,
    formValues.zipCode,
  ]);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setSubmitError(null);

    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors)?.length > 0) {
      return;
    }

    setSubmissionInFlight(true);
    try {
      const formattedAddress = [
        formValues.addressLine?.trim(),
        formValues.city?.trim(),
        formValues.zipCode?.trim(),
      ]
        .filter(Boolean)
        .join(', ');

      const trimmedFullName = formValues.fullName?.trim() ?? '';
      const trimmedPhone = formValues.phone?.trim() ?? '';

      let avatarImageBase64 = null;
      if (formValues.avatarImage) {
        avatarImageBase64 = await readFileAsDataUrl(formValues.avatarImage);
      }

      const onboardingPayload = {
        invitationToken: token,
        fullName: trimmedFullName,
        gender: formValues.gender,
        password: formValues.password,
        birthday: formValues.birthday || undefined,
        phone: trimmedPhone || undefined,
        address: formattedAddress || undefined,
        shiftPreference:
          formValues.shiftPreference?.length > 0
            ? formValues.shiftPreference.join(', ')
            : undefined,
      };

      if (avatarImageBase64) {
        onboardingPayload.avatarImageBase64 = avatarImageBase64;
        if (formValues.avatarImage?.name) {
          onboardingPayload.avatarImageName = formValues.avatarImage.name;
        }
        if (formValues.avatarImage?.type) {
          onboardingPayload.avatarImageType = formValues.avatarImage.type;
        }
      }

      await completeEmployeeOnboarding(onboardingPayload);

      if (invitation?.email) {
        try {
          await login({ email: invitation.email, password: formValues.password });
          navigate('/employee-self-service', { replace: true });
          return;
        } catch (authError) {
          setSubmitError(
            authError?.response?.data?.message ??
              authError?.message ??
              'Account created, but automatic sign-in failed. Please log in manually.',
          );
          navigate('/login', { replace: true });
          return;
        }
      }

      navigate('/login', { replace: true });
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message ??
          (Array.isArray(error?.response?.data?.message)
            ? error.response.data.message.join('. ')
            : error?.message ?? 'Unable to complete onboarding.'),
      );
    } finally {
      setSubmissionInFlight(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-theme min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card border border-border rounded-lg px-8 py-10 text-center shadow-elevation-2 w-full max-w-md">
          <LoadingState
            message="Loading your invitation…"
            heightClassName="min-h-[10rem]"
            spinnerSize="h-10 w-10"
          />
        </div>
      </div>
    );
  }

  if (fetchError || !invitation) {
    return (
      <div className="auth-theme min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card border border-border rounded-lg max-w-lg w-full px-8 py-10 text-center shadow-elevation-2">
          <Icon name="AlertTriangle" className="mx-auto mb-4 text-error" size={36} />
          <h1 className="text-2xl font-semibold text-foreground mb-2">Invitation Not Available</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {fetchError?.response?.data?.message ??
              (Array.isArray(fetchError?.response?.data?.message)
                ? fetchError.response.data.message.join('. ')
                : fetchError?.message ?? 'We could not locate an active invitation for this token.')}
          </p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-theme min-h-screen bg-background flex flex-col lg:flex-row">
      <AuthBrandingPanel widthClass="lg:w-2/5" className="px-8 lg:px-12">
        <div className="space-y-8 text-white">
          <div className="space-y-4 text-center lg:text-left">
            <p className="text-sm uppercase tracking-widest text-white/60 font-semibold">Ougama</p>
            <h1 className="text-3xl lg:text-4xl font-bold">Employee Onboarding</h1>
            <p className="text-white/70 leading-relaxed">
              Finish setting up your account to access scheduling, messaging, and all the tools you need for your first day.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4 shadow-elevation-1">
            <div className="flex items-center space-x-3">
              <Icon name="BriefcaseBusiness" size={18} className="text-white" />
              <div>
                <p className="text-xs uppercase tracking-wide text-white/60">Role</p>
                <p className="text-sm font-medium text-white">{normalizeInvitationValue(invitation?.role)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Building2" size={18} className="text-white" />
              <div>
                <p className="text-xs uppercase tracking-wide text-white/60">Department</p>
                <p className="text-sm font-medium text-white">
                  {normalizeInvitationValue(invitation?.department)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="MapPin" size={18} className="text-white" />
              <div>
                <p className="text-xs uppercase tracking-wide text-white/60">Location</p>
                <p className="text-sm font-medium text-white">{normalizeInvitationValue(invitation?.location)}</p>
              </div>
            </div>
            {invitation?.managerName && (
              <div className="flex items-center space-x-3">
                <Icon name="UserRound" size={18} className="text-white" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-white/60">Hiring Manager</p>
                  <p className="text-sm font-medium text-white">{normalizeInvitationValue(invitation.managerName)}</p>
                </div>
              </div>
            )}
            {invitation?.startDate && (
              <div className="flex items-center space-x-3">
                <Icon name="CalendarDays" size={18} className="text-white" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-white/60">Start Date</p>
                  <p className="text-sm font-medium text-white">
                    {new Date(invitation.startDate)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </AuthBrandingPanel>

      <section className="lg:w-3/5 p-8 lg:p-12 bg-card flex items-center">
        <div className="w-full max-w-xl mx-auto">
          <div className="lg:hidden mb-8 flex justify-center">
            <img
              src="/assets/images/logo-black-on-white.png"
              alt="Ougama"
              className="h-12 w-auto"
            />
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-foreground">Welcome aboard!</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Complete the form below to create your account. Your email address is already confirmed through your manager&apos;s invitation.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Invited Email</p>
              <p className="text-sm font-medium text-foreground break-all">
                {invitation?.email}
              </p>
            </div>

            <Input
              label="Full Name"
              value={formValues.fullName}
              onChange={handleInputChange('fullName')}
              required
              error={validationErrors?.fullName}
            />

            <DatePicker
              label="Birthday"
              value={formValues.birthday}
              onChange={handleInputChange('birthday')}
              required
              error={validationErrors?.birthday}
            />

            <Select
              label="Gender"
              placeholder="Select gender"
              options={[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
              ]}
              value={formValues.gender}
              onChange={handleGenderChange}
              required
              error={validationErrors?.gender}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Password"
                type="password"
                value={formValues.password}
                onChange={handleInputChange('password')}
                required
                error={validationErrors?.password}
                description={
                  formValues.password?.length >= 8
                    ? undefined
                    : 'Password must be at least 8 characters.'
                }
              />
              <Input
                label="Confirm Password"
                type="password"
                value={formValues.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                required
                error={validationErrors?.confirmPassword}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Phone"
                value={formValues.phone}
                onChange={handleInputChange('phone')}
                required
                error={validationErrors?.phone}
              />
              <Select
                label="Shift Preferences"
                placeholder="Select preferred shifts"
                multiple
                options={[
                  { label: 'Morning shift', value: 'Morning shift' },
                  { label: 'Afternoon shift', value: 'Afternoon shift' },
                  { label: 'Evening shift', value: 'Evening shift' },
                  { label: 'Night shift', value: 'Night shift' },
                ]}
                value={formValues.shiftPreference}
                onChange={handleShiftPreferenceChange}
                required
                error={validationErrors?.shiftPreference}
              />
            </div>

            <div className="space-y-4">
              <Input
                label="Home Address"
                value={formValues.addressLine}
                onChange={handleInputChange('addressLine')}
                required
                error={validationErrors?.addressLine}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City"
                  value={formValues.city}
                  onChange={handleInputChange('city')}
                  required
                  error={validationErrors?.city}
                />
                <Input
                  label="ZIP Code"
                  value={formValues.zipCode}
                  onChange={handleInputChange('zipCode')}
                  required
                  error={validationErrors?.zipCode}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Input
                label="Avatar Image"
                type="file"
                accept="image/*"
                onChange={handleFileChange('avatarImage')}
                required
                error={validationErrors?.avatarImage}
              />
              {formValues.avatarImage && (
                <p className="text-sm text-muted-foreground">{formValues.avatarImage.name}</p>
              )}
            </div>

            <div className="space-y-3">
              <Input
                label="Identity Document"
                type="file"
                onChange={handleFileChange('identityDocument')}
                error={validationErrors?.identityDocument}
              />
              {formValues.identityDocument && (
                <p className="text-sm text-muted-foreground">{formValues.identityDocument.name}</p>
              )}
            </div>

            <Checkbox
              label="I agree to the data privacy policy (DSVGO conformity)"
              checked={formValues.dataPrivacyApproval}
              onChange={handleInputChange('dataPrivacyApproval')}
              required
              error={validationErrors?.dataPrivacyApproval}
            />

            {submitError && (
              <div className="bg-error/10 border border-error/20 text-error text-sm rounded-lg p-4">
                {submitError}
              </div>
            )}

            <Button type="submit" loading={submissionInFlight} fullWidth>
              Complete Onboarding
            </Button>
          </form>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            Need help? Contact your manager or reply to the invitation email for assistance.
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnboardingPage;
